import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils/index';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = ({
  className,
  children,
  isError,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger> & {
  isError?: boolean;
}) => {
  const hasError = props['aria-invalid'] === true || !!isError;

  return (
    <SelectPrimitive.Trigger
      className={cn(
        'group rounded-input border-outline-input-default bg-fill-background-neutral hover:border-outline-input-hover placeholder:text-text-default-primary text-body-m-regular flex h-12 w-full items-center justify-between border px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-60 [&>span]:line-clamp-1 [&[data-state=open]_.chevron-icon]:rotate-180',
        'focus-visible:border-outline-focus focus-visible:border-2 focus-visible:px-[11px] focus-visible:py-[7px]',
        '[&[data-state=open]]:border-outline-focus [&[data-state=open]]:border-2 [&[data-state=open]]:px-[11px] [&[data-state=open]]:py-[7px]',
        'focus-visible:outline-0',
        hasError && 'border-outline-error border-2 px-[11px] py-[7px]',
        className,
      )}
      {...props}
    >
      {children}
      <div className='flex items-center gap-1'>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className='chevron-icon size-6 transition-transform duration-200' />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  );
};

const SelectScrollUpButton = ({ className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollUpButton>) => (
  <SelectPrimitive.ScrollUpButton className={cn('flex cursor-default items-center justify-center py-1', className)} {...props}>
    <ChevronUp className='size-4' />
  </SelectPrimitive.ScrollUpButton>
);

const SelectScrollDownButton = ({ className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollDownButton>) => (
  <SelectPrimitive.ScrollDownButton className={cn('flex cursor-default items-center justify-center py-1', className)} {...props}>
    <ChevronDown className='size-4' />
  </SelectPrimitive.ScrollDownButton>
);

const SelectContent = ({
  className,
  children,
  position = 'popper',
  portalContainer,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Content> & {
  portalContainer?: Element | DocumentFragment | null | undefined;
}) => (
  <SelectPrimitive.Portal container={portalContainer ?? document.body}>
    <SelectPrimitive.Content
      className={cn(
        'rounded-surface bg-fill-background-neutral text-text-default-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden border shadow-md',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      onCloseAutoFocus={(e) => e.preventDefault()}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn('p-1', position === 'popper' && 'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]')}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);

const SelectLabel = ({ className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label className={cn('text-body-m-semibold p-3', className)} {...props} />
);

const SelectItem = ({ className, children, disabled, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    className={cn(
      'bg-fill-background-neutral hover:bg-fill-background-light text-text-default-primary focus:bg-fill-background-light text-body-m-regular relative flex w-full cursor-default items-center justify-between gap-2 rounded-lg px-3 py-2.5 outline-hidden select-none data-disabled:pointer-events-none data-disabled:bg-transparent data-disabled:opacity-50',
      className,
    )}
    disabled={disabled}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator>
      <Check className='size-5' />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
);

const SelectSeparator = ({ className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator className={cn('bg-outline-neutral -mx-1 my-1 h-px', className)} {...props} />
);

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
